import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  LinearProgress,
  Collapse,
  Chip,
  Grid
} from "@mui/material";
import PlayerHeadshot from "./PlayerHeadshot";
import {
  getMatchStats,
  getPlayerStats,
  getPlayerStatValue,
  getTeamExternalId,
} from "src/utils/matchDetailsUtils";
import { getPositionLetter } from "src/utils/positionUtils";
import useTenantSportType from "src/hooks/useTenantSportType";

const PlayerStatsTableHeader = ({ sportType }) => (
  <TableRow>
    <TableCell>Player</TableCell>
    {getPlayerStats(sportType).map((stat) => (
      <TableCell align="center" key={stat.key}>
        {stat.label}
      </TableCell>
    ))}
  </TableRow>
);

const MatchDetailsDialog = ({ open, onClose, matchData, sportType }) => {
  const resolvedSportType = useTenantSportType(sportType);
  const [showRawData, setShowRawData] = useState(false);

  if (!matchData) return null;

  const { showMatch } = matchData;
  return (
    <Dialog
      fullWidth
      maxWidth="lg"
      open={open}
      onClose={onClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Match Details</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          {showMatch && showMatch.details && showMatch.stats && (
            <>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
                {/* Home Team */}
                <Grid item xs={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <PlayerHeadshot
                      row={{ externalRef: getTeamExternalId(showMatch.details, 'Home') }}
                      imageHash={showMatch.details.HomeTeamName}
                    />
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {showMatch.details.HomeTeamName}
                    </Typography>
                  </Box>
                </Grid>

                {/* Center - Score and Status */}
                <Grid item xs={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Chip
                      label={showMatch.status}
                      size="small"
                      sx={{
                        backgroundColor: (() => {
                          const status = showMatch.status?.toLowerCase();
                          if (status === 'live' || status === 'inprogress') return '#00BC6D';
                          if (status === 'upcoming' || status === 'scheduled') return '#FF8423';
                          if (status === 'finished' || status === 'completed') return '#AAAAAA';
                          return '#1976d2';
                        })(),
                        color: '#fff'
                      }}
                    />

                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h3" component="span">
                        {showMatch.details.HomeTeamScore}
                      </Typography>
                      <Typography variant="h3" component="span" sx={{ mx: 1 }}>
                        :
                      </Typography>
                      <Typography variant="h3" component="span">
                        {showMatch.details.AwayTeamScore}
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                      >
                        {showMatch.details.Minute || "--"}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontSize: "0.875rem", color: "text.secondary" }}
                      >
                        {showMatch.details.Period}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Away Team */}
                <Grid item xs={4}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    <PlayerHeadshot
                      row={{ externalRef: getTeamExternalId(showMatch.details, 'Away') }}
                      imageHash={showMatch.details.AwayTeamName}
                    />
                    <Typography variant="h5" sx={{ mt: 1 }}>
                      {showMatch.details.AwayTeamName}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          {showMatch?.stats && showMatch?.details && (
            <Box sx={{ mt: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {(() => {
                  const teamStats = showMatch.stats;
                  const home = teamStats.find(
                    (t) => t.TeamFeedId === showMatch.details.HomeTeamFeedId
                  );
                  const away = teamStats.find(
                    (t) => t.TeamFeedId === showMatch.details.AwayTeamFeedId
                  );

                  return getMatchStats(resolvedSportType).map(({ key, label }) => {
                    const homeValue = home?.[key] ?? 0;
                    const awayValue = away?.[key] ?? 0;
                    // Scale each row's bars relative to the larger side, so
                    // count stats and yardage stats both render meaningfully
                    const rowMax = Math.max(homeValue, awayValue, 1);
                    const getProgress = (val) => Math.min((val / rowMax) * 100, 100);

                    return (
                      <Box
                        key={key}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <Box sx={{ width: "40%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={getProgress(homeValue)}
                            sx={{
                              height: 10,
                              backgroundColor: "#eee",
                              borderRadius: 2,
                              transform: "rotate(180deg)",
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "red",
                              },
                            }}
                          />
                        </Box>
                        <Typography width="30px" textAlign="center">
                          {homeValue}
                        </Typography>

                        <Typography sx={{ minWidth: 170, textAlign: "center" }}>
                          {label}
                        </Typography>

                        <Typography width="30px" textAlign="center">
                          {awayValue}
                        </Typography>
                        <Box sx={{ width: "40%" }}>
                          <LinearProgress
                            variant="determinate"
                            value={getProgress(awayValue)}
                            sx={{
                              height: 10,
                              backgroundColor: "#eee",
                              borderRadius: 2,
                              "& .MuiLinearProgress-bar": {
                                backgroundColor: "black",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>
          )}

          <Grid container spacing={3} sx={{ mt: 4 }}>
            {showMatch?.stats?.map((teamStats, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {teamStats.TeamFeedId === showMatch.details.HomeTeamFeedId
                    ? showMatch.details.HomeTeamName
                    : showMatch.details.AwayTeamName}
                </Typography>

                <Table size="small">
                  <TableHead>
                    <PlayerStatsTableHeader sportType={resolvedSportType} />
                  </TableHead>
                  <TableBody>
                    {teamStats.Players?.map((player, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {(() => {
                              const playerName =
                                player.PlayerName || `Player ${index + 1}`;
                              const nameParts = playerName.split(" ");
                              const position = player.PlayerScoreablePosition ?? null;
                              const positionLetter = position !== null ? getPositionLetter(position) : "";

                              let formattedName;
                              if (nameParts.length >= 2) {
                                const firstName = nameParts[0];
                                const lastName = nameParts.slice(1).join(" ");
                                formattedName = `${firstName.charAt(0)}. ${lastName}`;
                              } else {
                                formattedName = playerName;
                              }

                              return (
                                <>
                                  {positionLetter && (
                                    <Chip
                                      label={positionLetter}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 'bold',
                                        minWidth: 30,
                                        backgroundColor: '#1976d2',
                                        color: '#fff'
                                      }}
                                    />
                                  )}
                                  <Typography variant="body2">{formattedName}</Typography>
                                </>
                              );
                            })()}
                          </Box>
                        </TableCell>
                        {getPlayerStats(resolvedSportType).map((stat) => (
                          <TableCell align="center" key={stat.key}>
                            {getPlayerStatValue(stat, player)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>
            ))}
          </Grid>
        </DialogContentText>

        {showMatch !== null && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowRawData(!showRawData)}
              sx={{ mb: 2 }}
            >
              {showRawData ? "Hide Raw Data" : "Show Raw Data"}
            </Button>
            <Collapse in={showRawData}>
              <DialogContentText id="alert-dialog-description">
                <div>
                  <pre>{JSON.stringify(showMatch.stats, null, 2)}</pre>
                </div>
              </DialogContentText>
            </Collapse>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatchDetailsDialog;
